import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useItem } from 'src/utils/CommonDataFetchingUtils/useItem';
import { setItemFormData } from 'src/store/actions/itemForm';
import {
  deleteItem as deleteItemAction,
  updateItem,
} from 'src/store/actions/items';
import { clearServiceRequests } from 'src/store/actions/serviceRequests';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { fetchCommonIssues } from 'src/store/actions/CommonIssues';
import { selectCommonIssues } from 'src/store/selectors/CommonIssues';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import { useMaintenanceGuides } from 'src/SharedCustomHooks/UseMaintenanceGuides/useMaintenanceGuides';
import {
  useSpecifications,
  UseSpecificationsReturn,
} from 'src/SharedCustomHooks/UseSpecifications/useSpecifications';
import useFaqOperations, {
  UseFaqOperationsReturn,
} from 'src/components/Faqs/CustomHooks/useFaqOperations';
import { UseMaintenanceGuidesReturn } from 'src/SharedCustomHooks/UseMaintenanceGuides/types';

export interface UseItemOrchestratorOptions {
  itemId: string;
  history: any;
  notes: any[];
}

export interface UseItemOrchestratorReturn {
  // Core.
  item: any;
  refetchItem: () => Promise<void>;
  user: any;
  commonIssues: any[];
  branding: any;
  roleName: string;

  // Edit form overlay.
  showOverlay: boolean;
  onOpenEditForm: () => void;
  onCloseEditForm: () => void;
  onSaveItem: (item: any) => void;

  // Delete.
  onDeleteItem: () => void;

  // FAQs.
  faqOperations: UseFaqOperationsReturn;

  // Notes (derived from passed-in notes).
  hasHomeOwnerVisibleNote: boolean;

  // Documents.
  documents: any[];
  loadingDocs: boolean;
  isSavingDoc: boolean;
  saveDocument: UseFaqsNotesDocumentsAPIReturn['saveDocument'];
  deleteDocument: UseFaqsNotesDocumentsAPIReturn['deleteDocument'];

  // Maintenance guides.
  maintenanceGuides: any[];
  maintenanceGuidesLoading: boolean;
  saveGuide: UseMaintenanceGuidesReturn['saveGuide'];
  updateGuide: UseMaintenanceGuidesReturn['updateGuide'];
  deleteGuide: UseMaintenanceGuidesReturn['deleteGuide'];
  maintenanceGuidesError: UseMaintenanceGuidesReturn['error'];
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Specifications.
  specificationsAPI: UseSpecificationsReturn;
}

export const useItemOrchestrator = ({
  itemId,
  history,
  notes,
}: UseItemOrchestratorOptions): UseItemOrchestratorReturn => {
  const dispatch = useDispatch();

  const { item, refetchItem } = useItem(itemId);

  const user: any = useSelector(selectAuthenticatedUser);
  const commonIssues: any[] = useSelector(selectCommonIssues);

  const { branding } = useContext(BrandingContext);

  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  const faqOperations: UseFaqOperationsReturn = useFaqOperations({
    itemId: item?.id,
    userId: user?.id,
    entityType: 'Item',
    assetId: null,
  });

  const hasHomeOwnerVisibleNote: boolean =
    Array.isArray(notes) &&
    notes.some((n: any) => n?.noteVisibleToHomeownerOccupantRealtor === true);

  const {
    documents,
    loadingDocs,
    isSaving: isSavingDoc,
    fetchDocuments,
    saveDocument,
    deleteDocument,
  }: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({
    customUploadErrorMessage:
      'Failed to upload this item document. Please try again or contact Juniper support if the issue persists.',
    user,
  });

  const {
    guides: maintenanceGuides,
    loading: maintenanceGuidesLoading,
    saveGuide,
    updateGuide,
    deleteGuide,
    error: maintenanceGuidesError,
    setSearchTerm,
    searchTerm,
  }: UseMaintenanceGuidesReturn = useMaintenanceGuides(item?.id, {
    source: 'Catalog',
    userId: user?.id,
    userFirstName: user?.firstname,
    userLastName: user?.lastname,
  });

  const specificationsAPI: UseSpecificationsReturn = useSpecifications(
    item?.id
  );

  useEffect(() => {
    if (!itemId) {
      return;
    }

    dispatch(clearServiceRequests());
    dispatch(fetchCommonIssues(itemId));
    void fetchDocuments(itemId);
  }, [itemId]);

  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const onOpenEditForm = (): void => {
    setShowOverlay(true);
    dispatch(
      setItemFormData({
        ...item,
        selectedProperties: item.properties.map((pty: any) => ({
          value: pty.id,
          label: pty.name,
        })),
      })
    );
  };

  const onCloseEditForm = (): void => {
    setShowOverlay(false);
  };

  const onSaveItem = async (updated: any): Promise<void> => {
    if (!updated.id) {
      return;
    }

    const properties: { id: any }[] = (updated.selectedProperties ?? []).map(
      (sp: any) => ({ id: sp.value })
    );

    const params = {
      id: updated.id,
      name: updated.name,
      MakeName: updated.makeName,
      model: updated.model,
      TypeId: updated.typeId,
      warrantyYears: updated.warrantyYears,
      UserId: user.id,
      properties,
    };

    await dispatch(updateItem(params) as any);
    await refetchItem();
  };

  const onDeleteItem = (): void => {
    if (!item) return;

    const confirmed: boolean = window.confirm(
      `Are you sure you want to delete ${item.name}?`
    );

    if (!confirmed) return;

    dispatch(deleteItemAction(item.id, history));
  };

  return {
    item,
    refetchItem,
    user,
    commonIssues,
    branding,
    roleName,

    showOverlay,
    onOpenEditForm,
    onCloseEditForm,
    onSaveItem,

    onDeleteItem,

    faqOperations,

    hasHomeOwnerVisibleNote,

    documents,
    loadingDocs,
    isSavingDoc,
    saveDocument,
    deleteDocument,

    maintenanceGuides,
    maintenanceGuidesLoading,
    saveGuide,
    updateGuide,
    deleteGuide,
    maintenanceGuidesError,
    searchTerm,
    setSearchTerm,

    specificationsAPI,
  };
};

export default useItemOrchestrator;
